import {
  fetchAllDevices,
  edit,
  remove,
  create,
  getConfigOptions,
  setConfigOptions
} from 'api/device';
import { REQUEST_SUCCESS, REQUEST_FAIL } from 'reducers/modal/actions';
import { fetchAllTopoNodeData } from 'reducers/toponode/actions';
import { getAllIndeNodes } from 'reducers/indenode/actions';
import { getAllAggregators } from 'reducers/aggregator/actions';

export const CREATE_DEVICE_SUCCESS = 'CREATE_DEVICE_SUCCESS';
export const GET_ALL_DEVICES_SUCCESS = 'GET_ALL_DEVICES_SUCCESS';
export const EDIT_DEVICE_SUCCESS = 'EDIT_DEVICE_SUCCESS';
export const DELETE_DEVICE_SUCCESS = 'DELETE_DEVICE_SUCCESS';
export const UPDATE_DEVICE_DATA = 'UPDATE_DEVICE_DATA';
export const SET_ALL_DEVICES_SUCCESS = 'SET_ALL_DEVICES_SUCCESS';
export const SET_INDIVIDUAL_DEVICE_SUCCESS = 'SET_INDIVIDUAL_DEVICE_SUCCESS';
export const SET_CONFIGURATION_SUCCESS = 'SET_CONFIGURATION_SUCCESS';
export const UPDATE_CONFIGURATION_OPTIONS_SUCCESS =
  'UPDATE_CONFIGURATION_OPTIONS_SUCCESS';
export const GET_ALL_DEVICES_FAIL = 'GET_ALL_DEVICES_FAIL';
export const CREATE_DEVICE_FAIL = 'CREATE_DEVICE_FAIL';
export const UPDATE_CONFIGURATION_OPTIONS_FAIL =
  'UPDATE_CONFIGURATION_OPTIONS_FAIL';
export const DUPLICATE_DEVICE_FAIL = 'DUPLICATE_DEVICE_FAIL';
export const getAllDevices = () => async dispatch => {
  try {
    const devices = await fetchAllDevices();
    return dispatch({
      type: GET_ALL_DEVICES_SUCCESS,
      data: devices
    });
  } catch (err) {
    // Redundant catch-throw, but maintaining this until we update polling
    throw err;
  }
};

export const duplicateDevice = deviceId => async (dispatch, getState) => {
  try {
    dispatch({
      type: REQUEST_FAIL,
      message: `Device with ID ${deviceId} already exists`
    });
  } catch (err) {
    return dispatch({
      type: DUPLICATE_DEVICE_FAIL,
      err
    });
  }
};

export const editDevice =
  (editedDevice, deviceId) => async (dispatch, getState) => {
    try {
      await edit(editedDevice, deviceId);
      // NOTE: refetch devices and dispatch after edit, will be revisited during polling epic
      const updatedDevices = await dispatch(getAllDevices());
      await dispatch(fetchAllTopoNodeData());
      await dispatch(getAllIndeNodes());
      await dispatch(getAllAggregators());
      dispatch({
        type: REQUEST_SUCCESS,
        message: `Device ${deviceId} edit success`
      });
      return updatedDevices;
    } catch (err) {
      const dispatchArg = {
        type: REQUEST_FAIL
      };

      if (err.status >= 500) {
        dispatchArg.message = `Device ${deviceId} edit failure`;
      } else {
        dispatchArg.message = err.message;
      }

      dispatch(dispatchArg);
      throw err;
    }
  };

export const deleteDevice = removedDeviceId => async (dispatch, getState) => {
  try {
    await remove(removedDeviceId);
    // NOTE: refetch devices and dispatch after delete, will be revisited during polling epic
    const updatedDevices = await dispatch(getAllDevices());
    await dispatch(fetchAllTopoNodeData());
    await dispatch(getAllIndeNodes());
    await dispatch(getAllAggregators());
    dispatch({
      type: REQUEST_SUCCESS,
      message: `Device ${removedDeviceId} delete success`
    });
    return updatedDevices;
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: `Device ${removedDeviceId} delete failed`
    });
    throw err;
  }
};

export const createDevice = createdDevice => async (dispatch, getState) => {
  try {
    await create(createdDevice);
    // NOTE: refetch devices and dispatch after create, will be revisited during optimization tasks
    await dispatch(getAllDevices());
    await dispatch(fetchAllTopoNodeData());
    await dispatch(getAllIndeNodes());
    await dispatch(getAllAggregators());
    const { sfdi } = getState().device.devices.find(
      device => device.deviceId === createdDevice.deviceId
    );
    dispatch({
      type: REQUEST_SUCCESS,
      message: `Device ${createdDevice.deviceId} create success`
    });
    return { sfdi };
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: `Device ${createdDevice.deviceId} create failed`
    });
    throw err;
  }
};

export const setDevices = devices => async dispatch => {
  try {
    return dispatch({
      type: SET_ALL_DEVICES_SUCCESS,
      data: devices
    });
  } catch (err) {
    // Redundant catch-throw, but maintaining this until we update polling
    throw err;
  }
};

export const setIndividualDevice =
  individualDevice => async (dispatch, getState) => {
    try {
      const devices = getState().device.devices;
      const updatedDevices = devices.map(device => {
        if (device.deviceId === individualDevice.deviceId) {
          return individualDevice;
        }
        return device;
      });
      await dispatch({
        type: SET_INDIVIDUAL_DEVICE_SUCCESS,
        data: updatedDevices
      });
      return;
    } catch (err) {
      // Redundant catch-throw, but maintaining this until we update polling
      throw err;
    }
  };

/**
 * fetch configuration options via API, then update redux store.
 */
export const updateConfigurationOptions = () => async (dispatch, getState) => {
  try {
    const configOptions = await getConfigOptions();
    dispatch({
      type: UPDATE_CONFIGURATION_OPTIONS_SUCCESS,
      data: configOptions
    });
    return;
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: 'Could not retrieve configuration options'
    });
    throw err;
  }
};

/**
 * Take the configOptions parameter, send it to backend via API, and set it in the redux store.
 * @param {Object} configOptions - an object holding the configuration options that get submitted
 * @param {boolean} showModal - boolean to show success/failure module in the GUI
 */
export const setConfigurationOptions =
  (configOptions, showModal = false) =>
  async (dispatch, getState) => {
    try {
      //Send to server, await good response.
      await setConfigOptions(configOptions);
      await dispatch({
        type: SET_CONFIGURATION_SUCCESS,
        data: configOptions
      });
      if (showModal) {
        dispatch({
          type: REQUEST_SUCCESS,
          message: `Configuration set success`
        });
      }
      return;
    } catch (err) {
      if (showModal) {
        dispatch({
          type: REQUEST_FAIL,
          message: `Configuration set failure`
        });
      }
      throw err;
    }
  };
